// @flow
import { get, sortBy, filter } from 'lodash';
import apartmentIcon from 'assets/images/apartment.png';
import apartmentIconWhite from 'assets/images/apartmentWhite.png';
import villaIcon from 'assets/images/villa.png';
import diningIcon from 'assets/images/diningIconBlack.png';
import diningIconPink from 'assets/images/diningIconPink.png';
import fitnessIcon from 'assets/images/fitnessIconBlack.png';
import fitnessIconPink from 'assets/images/fitnessIconPink.png';
import hospitalIcon from 'assets/images/hospitalsIconBlack.png';
import hospitalIconPink from 'assets/images/hospitalsIconPink.png';
import nightLifeIcon from 'assets/images/nightlifeIconBlack.png';
import nightLifeIconPink from 'assets/images/nightlifeIconPink.png';
import schoolIcon from 'assets/images/schoolIconBlack.png';
import schoolIconPink from 'assets/images/schoolIconPink.png';
import shoppingIcon from 'assets/images/shoppingIconBlack.png';
import shoppingIconPink from 'assets/images/shoppingIconPink.png';
import airportIcon from 'assets/images/airportIconBlack.png';
import airpirtIconPink from 'assets/images/airportIconPink.png';
import railwayStationIcon from 'assets/images/railwayStationIconBlack.png';
import railwayStationIconPink from 'assets/images/railwayStationIconPink.png';
import placeIcon from 'assets/images/placeIconBlack.png';
import placeIconPink from 'assets/images/placeIconPink.png';
import allFilterIcon from 'assets/images/allFilterIconBlack.svg';
import allFilterIconPink from 'assets/images/allFilterIconPink.svg';
import HospitalImage from 'assets/images/Hospital.jpg';
import FitnessImage from 'assets/images/Fitness.jpg';
import ShoppingImage from 'assets/images/Shopping.jpg';
import RestaurantImage from 'assets/images/Dining.jpg';
import SchoolImage from 'assets/images/School.jpg';
import HotelImage from 'assets/images/Hotel.jpg';
import MapDining from 'assets/images/dining-map.png';
import MapHospital from 'assets/images/hospital-map.png';
import MapFitness from 'assets/images/fitness-map.png';
import MapHotel from 'assets/images/hotel-map.png';
import MapSchool from 'assets/images/school-map.png';
import MapShopping from 'assets/images/shopping-map.png';
import MapAirport from 'assets/images/airport-map.png';
import MapRailwayStation from 'assets/images/railway-map.png';
import MapPlace from 'assets/images/place-map.png';
import constructionIcon from 'assets/images/constructionWhite.png';
import AirportImage from 'assets/images/Airport.png';
import RailwayStationImage from 'assets/images/RailwayStation.png';
import PlaceImage from 'assets/images/Place.png';
import upcomingIcon from 'assets/images/Icon---05.png';
import { MapUtil } from 'utils/MapUtil';
import { ProductMapper } from 'mappers/product';
import { StringUtil } from 'utils/StringUtil';
import type { ProjectFilters } from 'types/filter';
import type { MediaPath } from 'types/common';
import type {
  ConstructionGalleryType,
  Countries,
  Neighbourhood,
  Project,
  ProjectAnnouncement,
  ProjectDetails,
  ProjectOverlayDescriptionData,
} from 'types/project';
import moment from 'moment';

const mapProjectAnnouncements = (
  projectAnnouncements: any,
  mediaPath: MediaPath,
  vertical_id: number
): Array<ProjectAnnouncement> => {
  if (!projectAnnouncements || projectAnnouncements.length === 0) {
    return [];
  }

  const order = (a, b) => {
    return a.sortOrder - b.sortOrder;
  };

  if (!vertical_id) {
    // To filter announcements on Landing page.
    // Only announcements which are not related to any project will show on the Landing Page
    projectAnnouncements = filter(
      projectAnnouncements,
      ({ project_id }) => project_id === null
    );
  }

  return projectAnnouncements
    .map(announcement => mapProjectAnnouncement(announcement, mediaPath))
    .sort(order);
};

const mapProjectAnnouncement = (
  data: any,
  mediaPath: MediaPath
): ProjectAnnouncement => {
  return {
    id: data.id,
    title: data.announcement_heading,
    description: data.announcement_description,
    imageUrl: `${mediaPath.web}/${data.picture}`,
    mobileImageUrl: `${mediaPath.mobile}/${data.mobile_image}`,
    thumbNailUrl: `${mediaPath.thumbnail}/${data.picture}`,
    date: data.announced_on,
    isFeatured: get(data, 'is_featured', '').toLowerCase() === 'yes',
    isPublished: get(data, 'is_published', '').toLowerCase() === 'yes',
    sortOrder: data.sort_order,
    urlSlug: data.announcement_url,
  };
};

const fetchProjectStatusText = data => {
  switch (data?.project_status_id) {
    case 1:
      return 'ONGOING';
    case 2:
      return `COMPLETED ${moment(data.date_of_completion).format('YYYY')}`;
    case 3:
      return 'UPCOMING';
    default:
      return null;
  }
}

const mapProject = (
  data: any,
  mediaPath: MediaPath,
  countries: Countries
): Project => {
  let projectStatusText = fetchProjectStatusText(data);
  const detailsList = data.project_details || [];
  const details = detailsList.length > 0 ? detailsList[0] : {};
  const area = get(data, 'area.area_name', '');
  const city = get(data, 'city.city_name', '');
  const country = get(data, 'country.country_name', '');
  const location = area ? `${area}, ${city}` : `${city}, ${country}`;
  const products = ProductMapper.mapProducts(data.properties || []);
  const projectCompletionText = projectStatusText || '';
  const countryList = countries
    ? getProjectCountInCountry(data.country_id, country, countries)
    : null;
  return {
    id: data.id,
    name: details.project_name || '',
    description: details.project_description || '',
    project_list_description: details.project_list_description || '',
    short_description: details.project_short_description || '',
    urlSlug: data.url_slug,
    latitude: data.latitude,
    longitude: data.longitude,
    brochure_campaign_code: data.brochure_campaign_code,
    dateOfCompletion: data.date_of_completion,
    yearOfCompletion: new Date(data.date_of_completion).getFullYear(),
    amenityImageUrl: `${mediaPath.web}/${data.amenity_image}`,
    amenityMobileImageUrl: `${mediaPath.mobile}/${data.amenity_image}`,
    brochureUrl: `${mediaPath.web}/${data.project_brochure}`,
    listImageUrl: `${mediaPath.web}/${data.project_list_image}`,
    listMobileImageUrl: `${mediaPath.mobile}/${data.list_mobile_image}`,
    detailImageUrl: `${mediaPath.web}/${data.project_detail_image}`,
    thumbNail: `${mediaPath.thumbnail}/${data.project_list_image}`,
    thumbNailMobile: `${mediaPath.thumbnail}/${data.list_mobile_image}`,
    leader: data.project_leader,
    collaborator: data.project_collaborator,
    client: data.project_client,
    project_name:data.display_name ? data.display_name :'',
    isFeatured: get(data, 'is_featured', '').toLowerCase() === 'yes',
    statusId: data.project_status_id,
    areaId: data.area_id,
    cityId: data.city_id,
    countryId: data.country_id,
    propertyType: data.property_type,
    projectSizeId: data.project_size_id,
    copyrightText: data.copyright_text,
    sortOrder: data.sort_order,
    projectCompletionText,
    area,
    city,
    country,
    location,
    products,
  };
};

const mapProjectBrowser = (
  projects: any,
  mediaPath: MediaPath
): Object<ProjectDetails> => {
  if (!projects || projects.length === 0) {
    return [];
  }
  const data = {};
  const countries: Object<Countries> = {};
  data.list = projects.map(project =>
    mapProject(project, mediaPath, countries)
  );
  var sortedItems = sortBy(data.list, function(item) {
    return item.sortOrder;
  }).reverse();
  data.list = sortedItems;
  data.countries = countries;
  return data;
};

const getProjectCountInCountry = (countryId, name, countries) => {
  if (!countries[countryId]) {
    countries[countryId] = {
      latitude: MapUtil.getLatitude(name),
      longitude: MapUtil.getLongitude(name),
      projectCount: 1,
    };
  } else {
    countries[countryId].projectCount++;
  }
};

const mapProjectNeighbourhoods = (
  neighbourhoods: any,
  mediaPath: MediaPath
): Array<Neighbourhood> => {
  if (!neighbourhoods || neighbourhoods.length === 0) {
    return [];
  }
  return neighbourhoods.map(neighbourhood =>
    mapNeighbourhood(neighbourhood, mediaPath)
  );
};

const mapNeighbourhood = (data: any, mediaPath: MediaPath) => {
  let neighbourhoodId = data && data.neighbourhood_type_id;
  let card_image = '';

  switch (neighbourhoodId) {
    case 1:
      card_image = HospitalImage;
      break;
    case 2:
      card_image = SchoolImage;
      break;
    case 3:
      card_image = FitnessImage;
      break;
    case 4:
      card_image = RestaurantImage;
      break;
    case 5:
      card_image = ShoppingImage;
      break;
    case 6:
      card_image = RestaurantImage;
      break;
    case 7:
      card_image = HotelImage;
      break;
    case 8:
      card_image = AirportImage;
      break;
    case 9:
      card_image = RailwayStationImage;
      break;
    case 10:
      card_image = PlaceImage;
      break;
    default:
      return null;
  }

  return {
    id: data.id,
    cityId: data.city_id,
    countryId: data.country_id,
    distance: data.distance,
    latitude: data.latitude,
    longitude: data.longitude,
    name: data.name,
    neighbourhoodTypeId: data.neighbourhood_type_id,
    projectId: data.project_id,
    status: data.status,
    location: data.street_address,
    listImageUrl: `${mediaPath.mobile}/${data.picture}`,
    listMobileImageUrl: card_image,
  };
};

const neighbourhoodTypes = [
  {
    id: 1,
    title: 'Hospital',
    imageSource: hospitalIcon,
    activeImgSrc: hospitalIconPink,
    isActive: true,
    mapIcon: MapHospital,
  },
  {
    id: 2,
    title: 'School',
    imageSource: schoolIcon,
    activeImgSrc: schoolIconPink,
    isActive: true,
    mapIcon: MapSchool,
  },
  {
    id: 3,
    title: 'Fitness',
    imageSource: fitnessIcon,
    activeImgSrc: fitnessIconPink,
    isActive: true,
    mapIcon: MapFitness,
  },
  {
    id: 4,
    title: 'Dining',
    imageSource: diningIcon,
    activeImgSrc: diningIconPink,
    isActive: true,
    mapIcon: MapDining,
  },
  {
    id: 5,
    title: 'Shopping',
    imageSource: shoppingIcon,
    activeImgSrc: shoppingIconPink,
    isActive: true,
    mapIcon: MapShopping,
  },
  {
    id: 6,
    title: 'NightLife',
    imageSource: nightLifeIcon,
    activeImgSrc: nightLifeIconPink,
    isActive: true,
    mapIcon: MapDining,
  },
  {
    id: 7,
    title: 'Hotel',
    imageSource: diningIcon,
    activeImgSrc: diningIconPink,
    isActive: true,
    mapIcon: MapHotel,
  },
  {
    id: 8,
    title: 'Airport',
    imageSource: airportIcon,
    activeImgSrc: airpirtIconPink,
    isActive: true,
    mapIcon: MapAirport,
  },
  {
    id: 9,
    title: 'Railway Station',
    imageSource: railwayStationIcon,
    activeImgSrc: railwayStationIconPink,
    isActive: true,
    mapIcon: MapRailwayStation,
  },
  {
    id: 10,
    title: 'Place',
    imageSource: placeIcon,
    activeImgSrc: placeIconPink,
    isActive: true,
    mapIcon: MapPlace,
  },
];

const allNeighbourhoodFilter = {
  id: 'all',
  title: 'All',
  imageSource: allFilterIcon,
  activeImgSrc: allFilterIconPink,
  isActive: true,
};

const statusOptions = [
  {
    id: 2,
    title: 'Completed',
    color: 'green',
    imageSource: apartmentIconWhite,
  },
  { id: 1, title: 'Ongoing', color: 'red', imageSource: constructionIcon },
  { id: 3, title: 'Upcoming', color: 'yellow', imageSource: upcomingIcon },
];

const propertyTypeOptions = [
  { id: 1, title: 'Apartment', imageSource: apartmentIcon },
  { id: 2, title: 'Villa', imageSource: villaIcon },
];

const projectSize = [
  { id: 1, size: 'SMALL' },
  { id: 2, size: 'MEDIUM' },
  { id: 3, size: 'LARGE' },
  { id: 4, size: 'VERY LARGE' },
];

const extractProjectFilters = (projects: Array<Project>): ProjectFilters => {
  const locationsMap = {};
  projects.forEach(project => {
    const country = project.country;
    if (!locationsMap[country]) {
      locationsMap[country] = [];
    }
    const cityId = project.cityId;
    const city = project.city;

    if (locationsMap[country].findIndex(item => item.id === cityId) === -1) {
      locationsMap[country].push({ id: cityId, name: city });
    }
  });

  const locations = Object.keys(locationsMap).map(country => {
    return {
      country,
      cities: locationsMap[country],
    };
  });

  return {
    location: locations,
    projectStatus: [...statusOptions],
    propertyType: [...propertyTypeOptions],
    beds: {
      min: 1,
      max: 5,
    },
    size: {
      min: 1,
      max: 4,
    },
    year: {
      min: 1976,
      max: 2040,
    },
    projectSize,
  };
};

const mapConsturctionGalleryData = (
  constructionGalleryData: Array<any>,
  mediaPath: MediaPath
): Object<ConstructionGalleryType> => {
  if (!constructionGalleryData || constructionGalleryData.length === 0) {
    return [];
  }
  const projectDetails = constructionGalleryData[0].project.project_details[0];
  const constructionGalleryIntro = {
    projectName: projectDetails ? projectDetails.project_name : '',
    galleryHeading: projectDetails ? projectDetails.gallery_heading : '',
    galleryHeadingMob: projectDetails
      ? projectDetails.gallery_mobile_heading
      : '',
    galleryDescription: projectDetails
      ? projectDetails.gallery_description
      : '',
    galleryDescriptionMob: projectDetails
      ? projectDetails.gallery_mobile_description
      : '',
  };

  const constructionGalleryList = constructionGalleryData.map(
    constructionGallery =>
      mapConstructionGallery(constructionGallery, mediaPath)
  );

  if (constructionGalleryList) {
    return {
      constructionGalleryIntro,
      constructionGalleryList,
    };
  }

  return [];
};

const mapConstructionGallery = (
  constructionGallery: any,
  mediaPath: MediaPath
) => {
  const imageUrl = `${mediaPath.web}/${constructionGallery.picture}`;
  const listMobileImageUrl = `${mediaPath.mobile}/${constructionGallery.mobile_picture}`;
  const thumbnailUrl = `${mediaPath.thumbnail}/${constructionGallery.picture}`;
  const thumbnailMobileUrl = `${mediaPath.thumbnail}/${constructionGallery.mobile_picture}`;

  return {
    imageUrl,
    listMobileImageUrl,
    thumbnailUrl,
    thumbnailMobileUrl,
  };
};

// TODO: cleanup
const mapCompletedProjects = (completedProjects, mediaPath, type) => {
  let mobileData = [];
  if (completedProjects.length > 0) {
    for (let i = 0; i < completedProjects.length; i++) {
      if (type === 'completedProjects') {
        completedProjects[i].sub_heading = completedProjects[i]['project_name'];
        delete completedProjects[i].project_name;
      }
      if (type === 'ProjectBrochure') {
        completedProjects[i].sub_heading = completedProjects[i]['heading'];
        delete completedProjects[i].heading;
      }
      completedProjects[i].description =
        completedProjects[i]['project_description'];
      delete completedProjects[i].project_description;
    }
    completedProjects.forEach((data, idx) => {
      if (data.show_in_mobile === 'Yes') {
        let imagesToFilter = [
          data.mobile_picture1,
          data.mobile_picture2,
          data.mobile_picture3,
          data.mobile_picture4,
          data.mobile_picture5,
        ];
        const sectionData = {
          heading: data.sub_heading,
          description: data.project_mobile_description,
          images: imagesToFilter.filter(Boolean),
        };
        mobileData.push(sectionData);
      }
    });
    return { completedProjects, mobileData, mediaPath };
  }

  return [];
};

const mapProjectsForOverlay = (
  projects,
  mediaPath
): Array<ProjectOverlayDescriptionData> => {
  if (projects.length > 0) {
    let result = [];
    projects.map((project, idx) => {
      result.push(mapProjectOverlayData(project, mediaPath));
    });
    return result;
  }
  return [];
};

const mapProjectOverlayData = (project, mediaPath) => {
  let bannerImages = [],
    projectPlanImages = [],
    galleryImages = [];
  project.project_overlay_images.map(image => {
    let imageOutput = {};
    imageOutput = mapProjectOverlayImages(image, mediaPath);
    if (image.type === 'Banner') {
      bannerImages.push(imageOutput);
    }
    if (image.type === 'Gallery') {
      galleryImages.push(imageOutput);
    }
    if (image.type === 'Floor Plan') {
      projectPlanImages.push(imageOutput);
    }
  });
  return {
    bannerImages,
    galleryImages,
    projectPlanImages,
    project_id: project.project_id,
    urlSlug: project.project_url_slug,
    location: `${project.area.area_name}, ${project.city.city_name}`,
    area: project.area.area_name,
    areaId: project.area_id,
    city: project.city.city_name,
    cityId: project.city_id,
    descriptionOne: project.description_1,
    descriptionTwo: project.description_2,
    projectRichTextOne: project.plan_description_1,
    projectRichTextTwo: project.plan_description_2,
  };
};

const mapProjectOverlayImages = (imageData, mediaPath) => {
  return {
    description: imageData.description,
    image: `${mediaPath.web}/${imageData.image}`,
    mobile_image: `${mediaPath.mobile}/${imageData.mobile_image}`,
    thumbnail_desktop: `${mediaPath.thumbnail}/${imageData.image}`,
  };
};

export const ProjectMapper = {
  mapProjectAnnouncements,
  mapProjectBrowser,
  mapProjectNeighbourhoods,
  getProjectCountInCountry,
  extractProjectFilters,
  neighbourhoodTypes,
  allNeighbourhoodFilter,
  propertyTypeOptions,
  mapConsturctionGalleryData,
  mapCompletedProjects,
  mapProjectsForOverlay,
};
